
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Pack Opening Animation Component
const PackOpeningAnimation = () => {
  // Get the current animation type from the most recently created card
  const animationType = useRef("magicalPortal");
  
  // Set a random animation type
  useEffect(() => {
    const types = ["burstOpen", "magicalUnwrap", "magicalPortal",
      "explosionEffect", 
      "risingCards", 
      "spinningWheel"];
    animationType.current = types[Math.floor(Math.random() * types.length)];
  }, []);

  // Render the appropriate animation
  if (animationType.current === "burstOpen") {
    return (
      <>
        {/* Particle effects */}
        {Array(20).fill(20).map((_, i) => (
          <motion.div 
            key={i}
            className="absolute w-2 h-2 bg-yellow-300 rounded-full"
            initial={{ x: 0, y: 0, opacity: 1 }}
            animate={{ 
              x: (Math.random() - 0.5) * 300,
              y: (Math.random() - 0.5) * 300,
              opacity: 0
            }}
            transition={{ duration: 1 + Math.random() }}
          />
        ))}
      </>
    );
  } else if (animationType.current === "magicalUnwrap") {
    return (
      <>
        {/* Magical glow */}
        <motion.div 
          className="absolute inset-0 bg-white rounded-xl"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1.2, opacity: 0 }}
          transition={{ duration: 0.8 }}
        />
        
        {/* Unwrapping effect */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-indigo-100 to-purple-200 opacity-70"
          initial={{ scaleX: 1, scaleY: 0.1, y: 0 }}
          animate={{ scaleX: 0, scaleY: 1, y: -30 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-indigo-100 to-purple-200 opacity-70"
          initial={{ scaleX: 1, scaleY: 0.1, y: 0 }}
          animate={{ scaleX: 0, scaleY: 1, y: 30 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        />
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-indigo-100 to-purple-200 opacity-70"
          initial={{ scaleY: 1, scaleX: 0.1, x: 0 }}
          animate={{ scaleY: 0, scaleX: 1, x: -30 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        />
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-indigo-100 to-purple-200 opacity-70"
          initial={{ scaleY: 1, scaleX: 0.1, x: 0 }}
          animate={{ scaleY: 0, scaleX: 1, x: 30 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        />
      </>
    );
  } else if (animationType.current === "explosionEffect") {
    return (
      <>
        {/* Dramatic explosion effect */}
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-yellow-500 rounded-full"
          initial={{ scale: 0, opacity: 1 }}
          animate={{ 
            scale: 8, 
            opacity: 0,
            transition: { duration: 0.7 }
          }}
        />
        
        {/* Shockwave rings */}
        {[0, 1, 2].map((i) => (
          <motion.div 
            key={i}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-orange-500"
            initial={{ width: 10, height: 10, opacity: 0.8 }}
            animate={{ 
              width: 300, 
              height: 300, 
              opacity: 0,
              transition: { 
                duration: 0.8, 
                delay: i * 0.2
              }
            }}
          />
        ))}
        
        {/* Flying debris */}
        {Array(30).fill().map((_, i) => (
          <motion.div 
            key={i}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            initial={{ 
              opacity: 1, 
              scale: 1,
              rotate: Math.random() * 360
            }}
            animate={{ 
              x: (Math.random() - 0.5) * 400,
              y: (Math.random() - 0.5) * 400,
              opacity: 0,
              scale: Math.random() * 0.5 + 0.5,
              rotate: Math.random() * 360 + 180,
              transition: { 
                duration: 0.6 + Math.random() * 0.4
              }
            }}
          >
            <div 
              className={`w-2 h-2 ${
                Math.random() > 0.5 ? 'bg-orange-500' : 'bg-yellow-400'
              } rounded-sm`}
            />
          </motion.div>
        ))}
      </>
    );
  } else if (animationType.current === "risingCards") {
    return (
      <>
        {/* Rising smoke effect */}
        {Array(15).fill().map((_, i) => (
          <motion.div 
            key={i}
            className="absolute bottom-0 left-0 right-0 flex justify-center"
            initial={{ y: 0, opacity: 0 }}
            animate={{ 
              y: -100 - Math.random() * 100,
              opacity: [0, 0.4, 0],
              transition: { 
                duration: 2,
                delay: Math.random() * 0.5
              }
            }}
          >
            <div 
              className="w-8 h-8 bg-gray-200 rounded-full opacity-30 filter blur-md"
              style={{ 
                left: `${Math.random() * 80 + 10}%`
              }}
            />
          </motion.div>
        ))}
        
        {/* Glowing platform */}
        <motion.div 
          className="absolute bottom-0 left-1/4 right-1/4 h-1"
          initial={{ opacity: 0, scaleX: 0.5 }}
          animate={{ 
            opacity: [0, 0.8, 0],
            scaleX: 1,
            transition: {
              opacity: { 
                times: [0, 0.4, 1],
                duration: 1.5
              },
              scaleX: { duration: 0.5 }
            }
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400 to-transparent rounded-full blur-sm" />
        </motion.div>
        
        {/* Light beams */}
        {Array(8).fill().map((_, i) => (
          <motion.div 
            key={i}
            className="absolute bottom-0 w-1 bg-blue-300 opacity-20"
            style={{ 
              left: `${10 + i * 10}%`,
              transformOrigin: 'bottom',
              height: '100%'
            }}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ 
              scaleY: 1, 
              opacity: [0, 0.2, 0],
              transition: { 
                scaleY: { duration: 0.5, delay: 0.2 },
                opacity: { times: [0, 0.5, 1], duration: 1.2 }
              }
            }}
          />
        ))}
      </>
    );
  } else if (animationType.current === "spinningWheel") {
    return (
      <>
        {/* Spinning energy circle */}
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
        >
          {/* Spinning markers */}
          {Array(12).fill().map((_, i) => (
            <motion.div 
              key={i}
              className="absolute top-1/2 left-1/2 w-full h-4"
              style={{ 
                transformOrigin: 'center',
                rotate: `${i * 30}deg`
              }}
              animate={{ 
                rotate: `${i * 30 + 360}deg`,
                transition: { 
                  duration: 2, 
                  ease: "easeInOut",
                }
              }}
            >
              <motion.div 
                className="absolute right-0 w-4 h-4 bg-purple-500 rounded-full"
                animate={{ 
                  opacity: [1, 0.4, 1],
                  scale: [1, 1.2, 1],
                  transition: { 
                    duration: 1, 
                    repeat: 1,
                    repeatType: "reverse"
                  }
                }}
              />
            </motion.div>
          ))}
        </motion.div>
        
        {/* Rotating rings */}
        {[1, 2, 3].map((i) => (
          <motion.div 
            key={i}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-purple-400"
            style={{ 
              width: `${i * 60}px`,
              height: `${i * 60}px`,
              opacity: 0.3 - (i * 0.05)
            }}
            animate={{ 
              rotate: i % 2 === 0 ? 360 : -360,
              transition: { 
                duration: 3 - (i * 0.5), 
                ease: "linear",
              }
            }}
          />
        ))}
        
        {/* Central flash */}
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: [0, 4, 0],
            opacity: [0, 0.8, 0],
            transition: { 
              duration: 1, 
              times: [0, 0.2, 1],
              delay: 1.2
            }
          }}
        />
      </>
    );
  } else {
    // Default: Magic Portal
    return (
      <>
        {/* Portal rings */}
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border-4 border-blue-400 opacity-80"
          animate={{ 
            width: "150%",
            height: "150%",
            opacity: 0
          }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-blue-500 opacity-90"
          animate={{ 
            width: "120%",
            height: "120%",
            opacity: 0
          }}
          transition={{ duration: 1.2, ease: "easeOut", delay: 0.1 }}
        />
        
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Swirling particles */}
          {Array(12).fill(12).map((_, i) => (
            <motion.div 
              key={i}
              className="absolute w-2 h-2 bg-blue-300 rounded-full"
              style={{ 
                left: "50%", 
                top: "50%",
                opacity: 0.7 
              }}
              animate={{ 
                x: Math.cos(i * (Math.PI * 2 / 12)) * 60,
                y: Math.sin(i * (Math.PI * 2 / 12)) * 60,
                opacity: [0.7, 1, 0]
              }}
              transition={{ 
                duration: 1.5, 
                ease: "easeInOut",
                times: [0, 0.5, 1], 
                delay: i * 0.05
              }}
            />
          ))}
        </motion.div>
      </>
    );
  }
};



// Card suits and values for generating random cards
const suits = ['♥', '♦', '♠', '♣'];
const values = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const colors = {
  '♥': 'text-red-600',
  '♦': 'text-red-600',
  '♠': 'text-gray-800',
  '♣': 'text-gray-800',
};

// Deck styles definition - moved outside component to be globally accessible
const deckStyles = [
  {
    name: "Classic",
    deckColor: "from-indigo-600 to-purple-700",
    deckTextColor: "text-white",
    cardBack: "bg-gradient-to-br from-indigo-600 to-purple-700",
    cardBackDesign: (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute inset-2 border-2 border-white border-opacity-20 rounded-lg"></div>
        <div className="text-white text-opacity-20 text-6xl font-bold">♠</div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-white border-opacity-10 rounded-full"></div>
        </div>
      </div>
    )
  },
  {
    name: "Royal",
    deckColor: "from-yellow-600 to-red-700",
    deckTextColor: "text-white",
    cardBack: "bg-gradient-to-br from-red-700 to-yellow-600",
    cardBackDesign: (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute inset-2 border-2 border-yellow-300 border-opacity-30 rounded-lg"></div>
        <div className="text-yellow-300 text-opacity-30 text-5xl font-bold">♦</div>
        <div className="absolute inset-0">
          {Array(5).fill(5).map((_, i) => (
            <div 
              key={i} 
              className="absolute w-4 h-4 border-2 border-yellow-300 border-opacity-20 transform rotate-45"
              style={{
                top: `${15 + i * 15}%`,
                left: `${15 + i * 15}%`
              }}
            ></div>
          ))}
        </div>
      </div>
    )
  },
  {
    name: "Mystic",
    deckColor: "from-purple-800 to-blue-500",
    deckTextColor: "text-white",
    cardBack: "bg-gradient-to-br from-blue-800 to-purple-500",
    cardBackDesign: (
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20 backdrop-blur-sm"></div>
        <div className="absolute opacity-20">
          {Array(8).fill(8).map((_, i) => (
            <div 
              key={i} 
              className="absolute w-20 h-1 bg-cyan-300 rounded-full"
              style={{
                transform: `rotate(${i * 45}deg)`,
              }}
            ></div>
          ))}
        </div>
        <div className="relative w-12 h-12 border-2 border-cyan-300 border-opacity-40 rounded-full flex items-center justify-center">
          <div className="w-6 h-6 bg-cyan-300 bg-opacity-30 transform rotate-45"></div>
        </div>
      </div>
    )
  },
  {
    name: "Nature",
    deckColor: "from-green-700 to-emerald-500",
    deckTextColor: "text-white",
    cardBack: "bg-gradient-to-br from-green-800 to-emerald-400",
    cardBackDesign: (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute inset-2 border-2 border-green-200 border-opacity-20 rounded-lg"></div>
        <div className="text-green-200 text-opacity-20 text-5xl font-bold">♣</div>
        <div className="absolute inset-0">
          {Array(6).fill(6).map((_, i) => (
            <div 
              key={i} 
              className="absolute w-6 h-6"
              style={{
                top: `${15 + (i % 3) * 30}%`,
                left: `${15 + Math.floor(i / 3) * 60}%`
              }}
            >
              <svg viewBox="0 0 24 24" className="w-full h-full fill-current text-green-200 opacity-20">
                <path d="M12,8L10.67,8.09C9.81,7.07 7.4,4.5 5,4.5C5,4.5 3.03,7.46 4.96,11.41C4.41,12.24 4.07,12.67 4,13.66L2.07,13.95L2.28,14.93L4.04,14.67L4.18,15.38L2.61,16.32L3.08,17.21L4.53,16.32C5.68,18.76 8.59,20 12,20C15.41,20 18.32,18.76 19.47,16.32L20.92,17.21L21.39,16.32L19.82,15.38L19.96,14.67L21.72,14.93L21.93,13.95L20,13.66C19.93,12.67 19.59,12.24 19.04,11.41C20.97,7.46 19,4.5 19,4.5C16.6,4.5 14.19,7.07 13.33,8.09L12,8M9,11A1,1 0 0,1 10,12A1,1 0 0,1 9,13A1,1 0 0,1 8,12A1,1 0 0,1 9,11M15,11A1,1 0 0,1 16,12A1,1 0 0,1 15,13A1,1 0 0,1 14,12A1,1 0 0,1 15,11M12,14L13.5,17H10.5L12,14Z" />
              </svg>
            </div>
          ))}
        </div>
      </div>
    )
  }
];

  // The main component that handles the card deck, opening packs, and animations
const CardAnimations = () => {
  const [packsRemaining, setPacksRemaining] = useState(5);
  const [cooldown, setCooldown] = useState(0);
  const [cards, setCards] = useState([]);
  const [animationDemo, setAnimationDemo] = useState('main'); // Controls which demo to show
  const [deckStyle, setDeckStyle] = useState(0); // Controls deck style
  const [burstDirection, setBurstDirection] = useState('N'); // Controls the direction of card burst
  const cooldownInterval = useRef(null);
  
  // These variables are already declared elsewhere, so removing the duplicate declaration
  
  // Card suits and values for generating random cards
  const suits = ['♥', '♦', '♠', '♣'];
  const values = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
  const colors = {
    '♥': 'text-red-600',
    '♦': 'text-red-600',
    '♠': 'text-gray-800',
    '♣': 'text-gray-800',
  };
  
  // Deck styles
  const deckStyles = [
    {
      name: "Classic",
      deckColor: "from-indigo-600 to-purple-700",
      deckTextColor: "text-white",
      cardBack: "bg-gradient-to-br from-indigo-600 to-purple-700",
      cardBackDesign: (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute inset-2 border-2 border-white border-opacity-20 rounded-lg"></div>
          <div className="text-white text-opacity-20 text-6xl font-bold">♠</div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 border-4 border-white border-opacity-10 rounded-full"></div>
          </div>
        </div>
      )
    },
    {
      name: "Royal",
      deckColor: "from-yellow-600 to-red-700",
      deckTextColor: "text-white",
      cardBack: "bg-gradient-to-br from-red-700 to-yellow-600",
      cardBackDesign: (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute inset-2 border-2 border-yellow-300 border-opacity-30 rounded-lg"></div>
          <div className="text-yellow-300 text-opacity-30 text-5xl font-bold">♦</div>
          <div className="absolute inset-0">
            {Array(5).fill(5).map((_, i) => (
              <div 
                key={i} 
                className="absolute w-4 h-4 border-2 border-yellow-300 border-opacity-20 transform rotate-45"
                style={{
                  top: `${15 + i * 15}%`,
                  left: `${15 + i * 15}%`
                }}
              ></div>
            ))}
          </div>
        </div>
      )
    },
    {
      name: "Mystic",
      deckColor: "from-purple-800 to-blue-500",
      deckTextColor: "text-white",
      cardBack: "bg-gradient-to-br from-blue-800 to-purple-500",
      cardBackDesign: (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-20 backdrop-blur-sm"></div>
          <div className="absolute opacity-20">
            {Array(8).fill(8).map((_, i) => (
              <div 
                key={i} 
                className="absolute w-20 h-1 bg-cyan-300 rounded-full"
                style={{
                  transform: `rotate(${i * 45}deg)`,
                }}
              ></div>
            ))}
          </div>
          <div className="relative w-12 h-12 border-2 border-cyan-300 border-opacity-40 rounded-full flex items-center justify-center">
            <div className="w-6 h-6 bg-cyan-300 bg-opacity-30 transform rotate-45"></div>
          </div>
        </div>
      )
    },
    {
      name: "Nature",
      deckColor: "from-green-700 to-emerald-500",
      deckTextColor: "text-white",
      cardBack: "bg-gradient-to-br from-green-800 to-emerald-400",
      cardBackDesign: (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute inset-2 border-2 border-green-200 border-opacity-20 rounded-lg"></div>
          <div className="text-green-200 text-opacity-20 text-5xl font-bold">♣</div>
          <div className="absolute inset-0">
            {Array(6).fill(6).map((_, i) => (
              <div 
                key={i} 
                className="absolute w-6 h-6"
                style={{
                  top: `${15 + (i % 3) * 30}%`,
                  left: `${15 + Math.floor(i / 3) * 60}%`
                }}
              >
                <svg viewBox="0 0 24 24" className="w-full h-full fill-current text-green-200 opacity-20">
                  <path d="M12,8L10.67,8.09C9.81,7.07 7.4,4.5 5,4.5C5,4.5 3.03,7.46 4.96,11.41C4.41,12.24 4.07,12.67 4,13.66L2.07,13.95L2.28,14.93L4.04,14.67L4.18,15.38L2.61,16.32L3.08,17.21L4.53,16.32C5.68,18.76 8.59,20 12,20C15.41,20 18.32,18.76 19.47,16.32L20.92,17.21L21.39,16.32L19.82,15.38L19.96,14.67L21.72,14.93L21.93,13.95L20,13.66C19.93,12.67 19.59,12.24 19.04,11.41C20.97,7.46 19,4.5 19,4.5C16.6,4.5 14.19,7.07 13.33,8.09L12,8M9,11A1,1 0 0,1 10,12A1,1 0 0,1 9,13A1,1 0 0,1 8,12A1,1 0 0,1 9,11M15,11A1,1 0 0,1 16,12A1,1 0 0,1 15,13A1,1 0 0,1 14,12A1,1 0 0,1 15,11M12,14L13.5,17H10.5L12,14Z" />
                </svg>
              </div>
            ))}
          </div>
        </div>
      )
    }
  ];

  // Generate a random card
  const generateRandomCard = () => {
    const suit = suits[Math.floor(Math.random() * suits.length)];
    const value = values[Math.floor(Math.random() * values.length)];
    
    // Random placeholder image sizes and themes
    const placeholderThemes = ['animals', 'nature', 'abstract', 'tech', 'food'];
    const theme = placeholderThemes[Math.floor(Math.random() * placeholderThemes.length)];
    const width = 240;
    const height = 340;
    
    return { 
      id: Math.random().toString(36).substr(2, 9), 
      suit, 
      value, 
      color: colors[suit],
      isFlipped: true, // Start face down (true means back showing)
      isRevealed: false, // Track if card has been revealed
      animationVariant: Math.floor(Math.random() * 3), // Random animation variant
      deckStyle: deckStyle, // Current deck style
      isPlaceholder: Math.random() < 0.33, // 33% chance to be a placeholder image
      placeholderImage: `/api/placeholder/${width}/${height}?text=${theme}+card`
    };
  };

  // Direction coordinates for card distribution
  const directionCoords = {
    'N': { x1: -70, y1: -120, x2: 0, y2: -150, x3: 70, y3: -120 },
    'NE': { x1: 0, y1: -120, x2: 70, y2: -120, x3: 140, y3: -70 },
    'E': { x1: 70, y1: -70, x2: 140, y2: 0, x3: 70, y3: 70 },
    'SE': { x1: 70, y1: 70, x2: 120, y2: 70, x3: 70, y3: 120 },
    'S': { x1: -70, y1: 120, x2: 0, y2: 150, x3: 70, y3: 120 },
    'SW': { x1: -120, y1: 70, x2: -70, y2: 70, x3: -70, y3: 120 },
    'W': { x1: -140, y1: 0, x2: -70, y2: -70, x3: -70, y3: 70 },
    'NW': { x1: -140, y1: -70, x2: -70, y2: -120, x3: 0, y3: -120 }
  };

  // Open a new pack of cards
  const openPack = () => {
    if (packsRemaining > 0 && cooldown === 0) {
      // Remove current cards with an exit animation
      setCards([]);
      
      // Randomly select one of the three animations
      // Randomly select one of the animations - prioritize explosion and rising cards
      const animationTypes = [
        "explosionEffect",  // Higher chances by including twice
        "explosionEffect", 
        "risingCards",
        "risingCards",
        "burstOpen", 
        "magicalUnwrap", 
        "magicalPortal", 
        "spinningWheel"
      ];
      const selectedAnimation = animationTypes[Math.floor(Math.random() * animationTypes.length)];
      
      // Set cooldown and reduce packs
      setPacksRemaining(prev => prev - 1);
      setCooldown(10);
      
      // Start cooldown timer
      cooldownInterval.current = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Generate 3 new random cards with position data for selected direction
      // Delay to allow animation to complete first
      setTimeout(() => {
        const coords = directionCoords[burstDirection];
        const newCards = Array(3).fill(3).map((_, index) => {
          const card = generateRandomCard();
          
          // Start positions for cards based on animation type
          let startX = 0, startY = 0;
          
          if (selectedAnimation === "burstOpen") {
            // Burst from center
            startX = 0;
            startY = 0;
          } else if (selectedAnimation === "magicalUnwrap") {
            // Start stacked from center
            startX = 0;
            startY = 0;
          } else if (selectedAnimation === "magicalPortal") {
            // Start from center portal
            startX = 0;
            startY = 0;
          }
          
          // End positions based on direction
          let xPos, yPos;
          if (index === 0) {
            xPos = coords.x1;
            yPos = coords.y1; 
          } else if (index === 1) {
            xPos = coords.x2;
            yPos = coords.y2;
          } else {
            xPos = coords.x3;
            yPos = coords.y3;
          }
          
          return {
            ...card,
            startX,
            startY,
            xPos,
            yPos,
            rotate: (index - 1) * 15,
            animationType: selectedAnimation
          };
        });
        setCards(newCards);
      }, 500); // Delay card appearance to allow animation to play
    }
  };
  
  // Flip a card when clicked
  const flipCard = (id) => {
    setCards(cards.map(card => 
      card.id === id && !card.isRevealed ? 
        { ...card, isFlipped: false, isRevealed: true } : 
        card
    ));
  };
  
  // Clean up interval on component unmount
  useEffect(() => {
    return () => {
      if (cooldownInterval.current) {
        clearInterval(cooldownInterval.current);
      }
    };
  }, []);
  
  // Reset the demo
  const resetDemo = () => {
    setPacksRemaining(5);
    setCooldown(0);
    setCards([]);
    if (cooldownInterval.current) {
      clearInterval(cooldownInterval.current);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-8 bg-gradient-to-br from-blue-50 to-purple-50 min-h-screen">
      <h1 className="text-3xl font-bold text-center mb-8 text-indigo-800">Card Animation Showcase</h1>
      
      {/* Demo Navigation */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        <button 
          onClick={() => {resetDemo(); setAnimationDemo('main');}}
          className={`px-4 py-2 rounded-lg ${animationDemo === 'main' ? 'bg-indigo-600 text-white' : 'bg-indigo-200 text-indigo-800'}`}
        >
          Main Demo
        </button>
        <button 
          onClick={() => {resetDemo(); setAnimationDemo('openPack');}}
          className={`px-4 py-2 rounded-lg ${animationDemo === 'openPack' ? 'bg-indigo-600 text-white' : 'bg-indigo-200 text-indigo-800'}`}
        >
          Pack Opening
        </button>
        <button 
          onClick={() => {resetDemo(); setAnimationDemo('cardFlip');}}
          className={`px-4 py-2 rounded-lg ${animationDemo === 'cardFlip' ? 'bg-indigo-600 text-white' : 'bg-indigo-200 text-indigo-800'}`}
        >
          Card Flip
        </button>
        <button 
          onClick={() => {resetDemo(); setAnimationDemo('hoverEffects');}}
          className={`px-4 py-2 rounded-lg ${animationDemo === 'hoverEffects' ? 'bg-indigo-600 text-white' : 'bg-indigo-200 text-indigo-800'}`}
        >
          Hover Effects
        </button>
        <button 
          onClick={() => {resetDemo(); setAnimationDemo('directionalBurst');}}
          className={`px-4 py-2 rounded-lg ${animationDemo === 'directionalBurst' ? 'bg-indigo-600 text-white' : 'bg-indigo-200 text-indigo-800'}`}
        >
          Directional Burst
        </button>
      </div>

      {/* Main Demo */}
      {animationDemo === 'main' && (
        <>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              {/* Deck display */}
              {packsRemaining > 0 ? (
                <motion.div 
                  className="relative"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <button 
                    onClick={openPack}
                    disabled={cooldown > 0 || packsRemaining === 0}
                    className={`w-32 h-44 bg-gradient-to-br ${deckStyles[deckStyle].deckColor} rounded-xl shadow-xl flex items-center justify-center ${deckStyles[deckStyle].deckTextColor} font-bold border-4 border-white relative overflow-hidden
                              ${cooldown > 0 ? 'opacity-80 cursor-wait' : 'hover:shadow-2xl'}`}
                  >
                    {/* Card deck design */}
                    <div className="absolute inset-2 bg-gradient-to-br from-black to-transparent opacity-20 rounded-lg"></div>
                    <div className="absolute inset-2 rounded-lg flex items-center justify-center">
                      <div className="text-xl">{deckStyles[deckStyle].name}</div>
                    </div>
                    
                    {/* Deck pattern overlay */}
                    <div className="absolute inset-0 opacity-10">
                      {Array(10).fill(10).map((_, i) => (
                        <div key={i} className="absolute h-8 bg-white rounded-lg" style={{
                          width: '90%',
                          top: `${i * 3}px`,
                          left: '5%',
                          transform: `rotate(${(i % 2 === 0 ? 1 : -1) * (i / 2)}deg)`
                        }}></div>
                      ))}
                    </div>
                    
                    {/* Magic portal animation on click */}
                    {cooldown === 10 && (
                      <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-xl">
                        {/* Different pack opening animations */}
                        <PackOpeningAnimation />
                      </div>
                    )}
                    
                    {/* Game-style pack indicator in corner */}
                    <div className="absolute top-1 right-1 w-10 h-10 bg-yellow-400 rounded-full shadow-inner flex items-center justify-center z-10 border-2 border-yellow-600">
                      <div className="relative">
                        <div className="absolute -top-1 -left-1 w-5 h-7 bg-white rounded-md border border-gray-300 transform -rotate-6"></div>
                        <div className="absolute -top-2 -left-2 w-5 h-7 bg-white rounded-md border border-gray-300 transform -rotate-12"></div>
                        <div className="absolute -top-3 -left-3 w-5 h-7 bg-white rounded-md border border-gray-300 transform -rotate-18"></div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-sm font-black text-indigo-800">
                          {packsRemaining}
                        </div>
                      </div>
                    </div>
                    
                    {/* Deck style cycle button */}
                    <div 
                      className="absolute bottom-1 left-1 w-8 h-8 bg-black bg-opacity-30 rounded-full flex items-center justify-center cursor-pointer z-20"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeckStyle((prev) => (prev + 1) % deckStyles.length);
                      }}
                    >
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    
                    {/* Direction selector */}
                    <div className="absolute bottom-1 right-1 w-8 h-8">
                      <div 
                        className="absolute inset-0 bg-black bg-opacity-30 rounded-full flex items-center justify-center cursor-pointer z-20"
                        onClick={(e) => {
                          e.stopPropagation();
                          const directions = Object.keys(directionCoords);
                          const currentIndex = directions.indexOf(burstDirection);
                          const nextIndex = (currentIndex + 1) % directions.length;
                          setBurstDirection(directions[nextIndex]);
                        }}
                      >
                        <div className="text-white text-xs font-bold">{burstDirection}</div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Cooldown overlay */}
                  {cooldown > 0 && (
                    <div className="absolute inset-0 bg-black bg-opacity-30 rounded-xl flex items-center justify-center">
                      <div className="text-white text-xl font-bold">{cooldown}s</div>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div 
                  className="w-32 h-44 bg-gray-300 rounded-xl shadow-md flex items-center justify-center text-gray-500 font-bold border-2 border-gray-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="relative flex items-center justify-center">
                    <div className="text-lg">EMPTY</div>
                    <div className="absolute -top-4 -right-4 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-red-600">
                      0
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
            
            <button 
              onClick={resetDemo}
              className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
            >
              Reset Demo
            </button>
          </div>
          
          {/* Cards display area */}
          <div className="flex justify-center items-center gap-6 h-64 mb-8">
            <AnimatePresence>
              {cards.map((card, index) => (
                <Card 
                  key={card.id}
                  card={card}
                  index={index}
                  onClick={() => flipCard(card.id)}
                />
              ))}
            </AnimatePresence>
          </div>
          
          <div className="text-center text-gray-600 mt-4">
            {cards.length === 0 && packsRemaining > 0 && "Click on the deck to open a pack of cards!"}
            {cards.length > 0 && "Click on a face-down card to reveal it. Once revealed, cards cannot be flipped back."}
            {packsRemaining === 0 && cards.length === 0 && "All packs have been opened. Reset the demo to start again."}
          </div>
        </>
      )}

      {/* Pack Opening Demo */}
      {animationDemo === 'openPack' && (
        <PackOpeningDemo />
      )}

      {/* Card Flip Demo */}
      {animationDemo === 'cardFlip' && (
        <CardFlipDemo />
      )}

      {/* Hover Effects Demo */}
      {animationDemo === 'hoverEffects' && (
        <HoverEffectsDemo />
      )}

      {/* Directional Burst Demo */}
      {animationDemo === 'directionalBurst' && (
        <DirectionalBurstDemo />
      )}
    </div>
  );
};

// Card Animation Component
const Card = ({ card, index, onClick }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 20
    });
  };
  
  // Determine entrance animation based on card properties
  let entranceAnimation = {};
  
  if (card.startX !== undefined && card.startY !== undefined) {
    // Custom animation based on the selected pack opening type
    if (card.animationType === "burstOpen") {
      entranceAnimation = {
        initial: { 
          x: 0, 
          y: 0, 
          scale: 0.5, 
          opacity: 0,
          rotate: 0
        },
        animate: { 
          x: card.xPos, 
          y: card.yPos, 
          scale: 1, 
          opacity: 1,
          rotate: card.rotate,
          transition: { 
            type: "spring",
            stiffness: 300,
            damping: 20,
            delay: index * 0.1
          }
        }
      };
    } else if (card.animationType === "magicalUnwrap") {
      entranceAnimation = {
        initial: { 
          x: 0, 
          y: 0, 
          opacity: 0,
          rotateY: 90
        },
        animate: { 
          x: card.xPos, 
          y: card.yPos, 
          opacity: 1,
          rotateY: 0,
          transition: { 
            type: "spring",
            stiffness: 200,
            damping: 15,
            delay: 0.3 + index * 0.2
          }
        }
      };
    } else if (card.animationType === "magicalPortal") {
      entranceAnimation = {
        initial: { 
          x: 0, 
          y: 0, 
          scale: 0, 
          opacity: 0 
        },
        animate: { 
          x: card.xPos, 
          y: card.yPos, 
          scale: 1, 
          opacity: 1,
          rotate: card.rotate,
          transition: { 
            type: "spring",
            stiffness: 250,
            damping: 20,
            delay: 0.7 + index * 0.2
          }
        }
      };
    }
  } else {
    // Default animations when not from pack opening
    const entranceVariants = [
      {
        // Fan out from center or use directional burst coordinates
        initial: { x: 0, y: 50, rotate: 0, opacity: 0 },
        animate: { 
          x: card.xPos !== undefined ? card.xPos : (index - 1) * 120, 
          y: card.yPos !== undefined ? card.yPos : 0, 
          rotate: card.rotate !== undefined ? card.rotate : (index - 1) * 5, 
          opacity: 1,
          transition: { 
            type: "spring", 
            stiffness: 300, 
            damping: 20,
            delay: index * 0.1 + 0.2
          }
        },
        exit: { 
          y: 100, 
          opacity: 0, 
          rotate: (index - 1) * 10,
          transition: { duration: 0.3 }
        }
      },
      {
        // Pop up with bounce
        initial: { scale: 0, opacity: 0, y: 20 },
        animate: { 
          x: card.xPos !== undefined ? card.xPos : (index - 1) * 120,
          y: card.yPos !== undefined ? card.yPos : 0,
          scale: 1, 
          opacity: 1,
          rotate: card.rotate !== undefined ? card.rotate : (index - 1) * 5,
          transition: { 
            type: "spring", 
            stiffness: 400, 
            damping: 15,
            delay: index * 0.15 + 0.3
          }
        },
        exit: { 
          scale: 0.8, 
          opacity: 0, 
          transition: { duration: 0.3 }
        }
      },
      {
        // Slide in from sides
        initial: { 
          x: (index - 1) * 300, 
          opacity: 0,
          rotate: (index - 1) * 15
        },
        animate: { 
          x: card.xPos !== undefined ? card.xPos : (index - 1) * 120,
          y: card.yPos !== undefined ? card.yPos : 0,
          opacity: 1,
          rotate: card.rotate !== undefined ? card.rotate : (index - 1) * 5,
          transition: { 
            type: "spring", 
            stiffness: 300, 
            damping: 20,
            delay: index * 0.1
          }
        },
        exit: { 
          x: (index - 1) * 300, 
          opacity: 0, 
          transition: { duration: 0.4 }
        }
      }
    ];
    
    entranceAnimation = entranceVariants[card.animationVariant % entranceVariants.length];
  }
  
  // Combined hover effect with both magnetic pull and shake
  const combinedHoverAnimation = isHovered ? {
    // Magnetic pull effect
    rotateY: card.isFlipped ? mousePosition.x : 0, // Only apply to face down cards
    rotateX: card.isFlipped ? -mousePosition.y : 0,
    // Shake effect - only for face down cards
    x: card.isFlipped ? [0, -2, 2, -2, 2, 0] : 0,
    y: card.isFlipped ? [0, 1, -1, 1, -1, 0] : 0,
    rotate: card.isFlipped ? [0, -0.5, 1, -1, 0.5, 0] : 0,
    // Scale effect
    scale: 1.05,
    z: 20,
    transition: {
      x: { repeat: Infinity, duration: 1, repeatDelay: 1 },
      y: { repeat: Infinity, duration: 1, repeatDelay: 1 },
      rotate: { repeat: Infinity, duration: 1, repeatDelay: 1 }
    }
  } : {};

  // Determine if card is clickable (only face down cards that haven't been revealed)
  const isClickable = card.isFlipped && !card.isRevealed;
  
  // Get current deck style for this card
  const currentStyle = deckStyles[card.deckStyle];

  return (
    <motion.div
      className="relative"
      variants={entranceAnimation}
      initial="initial"
      animate="animate"
      exit="exit"
      onMouseMove={handleMouseMove}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <motion.div
        className={`w-24 h-36 rounded-lg shadow-lg ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={() => isClickable && onClick()}
        animate={{ 
          rotateY: card.isFlipped ? 180 : 0,
          ...combinedHoverAnimation
        }}
        transition={{ 
          rotateY: { duration: 0.6, type: "spring", stiffness: 300, damping: 15 }
        }}
        style={{ perspective: "1000px" }}
      >
        {/* Card Front (face-up) */}
        <motion.div
          className={`absolute w-full h-full bg-white rounded-lg border-2 border-gray-200 flex flex-col items-center justify-between p-2
                     ${card.isFlipped ? 'opacity-0' : 'opacity-100'}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className={`text-xl font-bold ${card.color}`}>{card.value}</div>
          <div className={`text-4xl ${card.color}`}>{card.suit}</div>
          <div className={`text-xl font-bold ${card.color} transform rotate-180`}>{card.value}</div>
        </motion.div>

        {/* Card Back (face-down) */}
        <motion.div
          className={`absolute w-full h-full ${currentStyle.cardBack} rounded-lg border-2 border-gray-200 overflow-hidden`}
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          {isClickable && (
            <motion.div 
              className="absolute inset-0 bg-white bg-opacity-10"
              animate={{ opacity: [0.1, 0.2, 0.1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}
          
          {/* Decorative card back based on deck style */}
          <div className="absolute inset-0">
            {/* Card border */}
            <div className="absolute inset-2 border-2 border-white border-opacity-30 rounded-lg"></div>
            
            {/* Center emblem */}
            <div className="absolute inset-0 flex items-center justify-center">
              {currentStyle.name === "Classic" && (
                <>
                  <div className="text-white text-opacity-30 text-5xl font-bold">♠</div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 border-4 border-white border-opacity-20 rounded-full"></div>
                  </div>
                </>
              )}
              
              {currentStyle.name === "Royal" && (
                <>
                  <div className="text-yellow-300 text-opacity-40 text-5xl font-bold">♦</div>
                  <div className="absolute inset-0">
                    {Array(4).fill(4).map((_, i) => (
                      <div 
                        key={i} 
                        className="absolute w-4 h-4 border-2 border-yellow-300 border-opacity-30 transform rotate-45"
                        style={{
                          top: `${20 + i * 15}%`,
                          left: `${20 + i * 15}%`
                        }}
                      ></div>
                    ))}
                  </div>
                </>
              )}
              
              {currentStyle.name === "Mystic" && (
                <>
                  <div className="relative w-12 h-12 border-2 border-cyan-300 border-opacity-50 rounded-full flex items-center justify-center">
                    <div className="w-6 h-6 bg-cyan-300 bg-opacity-40 transform rotate-45"></div>
                  </div>
                  <div className="absolute inset-0 opacity-30">
                    {Array(8).fill(8).map((_, i) => (
                      <div 
                        key={i} 
                        className="absolute w-full h-0.5 bg-cyan-300 left-0 top-1/2 origin-center"
                        style={{ transform: `rotate(${i * 45}deg)` }}
                      ></div>
                    ))}
                  </div>
                </>
              )}
              
              {currentStyle.name === "Nature" && (
                <>
                  <div className="text-green-200 text-opacity-40 text-5xl font-bold">♣</div>
                  <div className="absolute inset-0">
                    {Array(4).fill(4).map((_, i) => (
                      <div 
                        key={i} 
                        className="absolute w-6 h-6"
                        style={{
                          top: `${15 + (i % 2) * 50}%`,
                          left: `${15 + Math.floor(i / 2) * 50}%`
                        }}
                      >
                        <svg viewBox="0 0 24 24" className="w-full h-full fill-current text-green-200 opacity-30">
                          <path d="M12,8L10.67,8.09C9.81,7.07 7.4,4.5 5,4.5C5,4.5 3.03,7.46 4.96,11.41C4.41,12.24 4.07,12.67 4,13.66L2.07,13.95L2.28,14.93L4.04,14.67L4.18,15.38L2.61,16.32L3.08,17.21L4.53,16.32C5.68,18.76 8.59,20 12,20C15.41,20 18.32,18.76 19.47,16.32L20.92,17.21L21.39,16.32L19.82,15.38L19.96,14.67L21.72,14.93L21.93,13.95L20,13.66C19.93,12.67 19.59,12.24 19.04,11.41C20.97,7.46 19,4.5 19,4.5C16.6,4.5 14.19,7.07 13.33,8.09L12,8M9,11A1,1 0 0,1 10,12A1,1 0 0,1 9,13A1,1 0 0,1 8,12A1,1 0 0,1 9,11M15,11A1,1 0 0,1 16,12A1,1 0 0,1 15,13A1,1 0 0,1 14,12A1,1 0 0,1 15,11M12,14L13.5,17H10.5L12,14Z" />
                        </svg>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Pattern overlay */}
            <div className="absolute inset-0">
              {currentStyle.name === "Classic" && (
                <div className="absolute inset-0 flex flex-wrap items-center justify-center opacity-10">
                  {Array(20).fill(20).map((_, i) => (
                    <div key={i} className="w-3 h-3 m-1 bg-white rounded-full"></div>
                  ))}
                </div>
              )}
              
              {currentStyle.name === "Royal" && (
                <div className="absolute inset-0 flex flex-col opacity-20">
                  {Array(6).fill(6).map((_, i) => (
                    <div key={i} className="flex-1 border-b border-t border-yellow-300"></div>
                  ))}
                </div>
              )}
              
              {currentStyle.name === "Mystic" && (
                <div className="absolute inset-0">
                  {Array(30).fill(30).map((_, i) => (
                    <div 
                      key={i} 
                      className="absolute w-1 h-1 bg-cyan-300 rounded-full opacity-30"
                      style={{
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`
                      }}
                    ></div>
                  ))}
                </div>
              )}
              
              {currentStyle.name === "Nature" && (
                <div className="absolute inset-0 flex flex-wrap content-around justify-around opacity-10">
                  {Array(12).fill(12).map((_, i) => (
                    <div key={i} className="w-4 h-4 m-1 border border-green-200 rounded-full"></div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Edge details */}
            <div className="absolute inset-x-0 top-0 h-6 flex justify-between items-center px-2">
              <div className={`text-xs font-bold ${
                currentStyle.name === "Classic" ? "text-white" : 
                currentStyle.name === "Royal" ? "text-yellow-300" : 
                currentStyle.name === "Mystic" ? "text-cyan-300" : 
                "text-green-200"
              } opacity-40`}>
                {currentStyle.name.charAt(0)}
              </div>
              <div className={`text-xs font-bold ${
                currentStyle.name === "Classic" ? "text-white" : 
                currentStyle.name === "Royal" ? "text-yellow-300" : 
                currentStyle.name === "Mystic" ? "text-cyan-300" : 
                "text-green-200"
              } opacity-40`}>
                {currentStyle.name.charAt(0)}
              </div>
            </div>
            
            <div className="absolute inset-x-0 bottom-0 h-6 flex justify-between items-center px-2 transform rotate-180">
              <div className={`text-xs font-bold ${
                currentStyle.name === "Classic" ? "text-white" : 
                currentStyle.name === "Royal" ? "text-yellow-300" : 
                currentStyle.name === "Mystic" ? "text-cyan-300" : 
                "text-green-200"
              } opacity-40`}>
                {currentStyle.name.charAt(0)}
              </div>
              <div className={`text-xs font-bold ${
                currentStyle.name === "Classic" ? "text-white" : 
                currentStyle.name === "Royal" ? "text-yellow-300" : 
                currentStyle.name === "Mystic" ? "text-cyan-300" : 
                "text-green-200"
              } opacity-40`}>
                {currentStyle.name.charAt(0)}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Pack Opening Demo Component
const PackOpeningDemo = () => {
  const [isOpening, setIsOpening] = useState(false);
  const [openingVariant, setOpeningVariant] = useState(0);
  
  const packOpeningVariants = [
    {
      name: "Burst Open",
      animation: (
        <motion.div className="relative w-48 h-64 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl">
          {isOpening && (
            <>
              {/* Particle effects */}
              {Array(20).fill(20).map((_, i) => (
                <motion.div 
                  key={i}
                  className="absolute w-2 h-2 bg-yellow-300 rounded-full"
                  initial={{ x: 0, y: 0, opacity: 1 }}
                  animate={{ 
                    x: (Math.random() - 0.5) * 300,
                    y: (Math.random() - 0.5) * 300,
                    opacity: 0
                  }}
                  transition={{ duration: 1 + Math.random() }}
                />
              ))}
            
              {/* Cards flying out */}
              <motion.div 
                className="absolute w-20 h-32 bg-white rounded-lg shadow-lg"
                initial={{ x: 0, y: 0, rotate: 0, scale: 0.5, opacity: 0 }}
                animate={{ x: -100, y: -50, rotate: -15, scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 20 }}
              />
              <motion.div 
                className="absolute w-20 h-32 bg-white rounded-lg shadow-lg"
                initial={{ x: 0, y: 0, rotate: 0, scale: 0.5, opacity: 0 }}
                animate={{ x: 0, y: -80, rotate: 0, scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 20 }}
              />
              <motion.div 
                className="absolute w-20 h-32 bg-white rounded-lg shadow-lg"
                initial={{ x: 0, y: 0, rotate: 0, scale: 0.5, opacity: 0 }}
                animate={{ x: 100, y: -50, rotate: 15, scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 20 }}
              />
            </>
          )}
        </motion.div>
      )
    },
    {
      name: "Magical Unwrap",
      animation: (
        <motion.div className="relative w-48 h-64">
          {/* Pack */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl shadow-xl"
            animate={isOpening ? { scale: 1.1, opacity: 0 } : { scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
          
          {/* Opening effect */}
          {isOpening && (
            <>
              {/* Magical glow */}
              <motion.div 
                className="absolute inset-0 bg-white rounded-xl"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1.2 }}
                transition={{ duration: 0.3 }}
              />
              
              {/* Cards appearing */}
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                initial={{ y: 0, opacity: 0, rotateY: 90 }}
                animate={{ y: -80, opacity: 1, rotateY: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              />
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                initial={{ y: 0, opacity: 0, rotateY: 90 }}
                animate={{ y: -40, x: -60, opacity: 1, rotateY: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              />
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                initial={{ y: 0, opacity: 0, rotateY: 90 }}
                animate={{ y: -40, x: 60, opacity: 1, rotateY: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              />
            </>
          )}
        </motion.div>
      )
    },
    {
      name: "Tear Open",
      animation: (
        <motion.div className="relative w-48 h-64">
          {/* Pack front */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl overflow-hidden"
            animate={isOpening ? { 
              rotateY: 180,
              transition: { duration: 0.5 }
            } : {}}
          >
            <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-xl">PACK</div>
          </motion.div>
          
          {/* Pack back (opened) */}
          {isOpening && (
            <>
              <motion.div 
                className="absolute left-0 top-0 w-1/2 h-full bg-gray-300 rounded-l-xl"
                initial={{ rotateY: 180 }}
                animate={{ rotateY: 120 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              />
              
              <motion.div 
                className="absolute right-0 top-0 w-1/2 h-full bg-gray-300 rounded-r-xl"
                initial={{ rotateY: 180 }}
                animate={{ rotateY: 240 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              />
              
              {/* Cards */}
              <motion.div 
                className="absolute w-16 h-28 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-md"
                initial={{ y: 0, scale: 0.5, opacity: 0 }}
                animate={{ y: -50, scale: 1, opacity: 1 }}
                transition={{ delay: 1, type: "spring" }}
              />
              <motion.div 
                className="absolute w-16 h-28 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-md transform -rotate-6"
                initial={{ y: 0, scale: 0.5, opacity: 0 }}
                animate={{ y: -30, x: -40, scale: 1, opacity: 1 }}
                transition={{ delay: 1.1, type: "spring" }}
              />
              <motion.div 
                className="absolute w-16 h-28 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-md transform rotate-6"
                initial={{ y: 0, scale: 0.5, opacity: 0 }}
                animate={{ y: -30, x: 40, scale: 1, opacity: 1 }}
                transition={{ delay: 1.2, type: "spring" }}
              />
            </>
          )}
        </motion.div>
      )
    },
    {
      name: "Magical Portal",
      animation: (
        <motion.div className="relative w-48 h-64">
          {/* Pack */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-indigo-800 to-purple-900 rounded-xl shadow-xl"
            initial={{ opacity: 1 }}
            animate={isOpening ? { opacity: 1 } : { opacity: 1 }}
          />
          
          {/* Portal effect */}
          {isOpening && (
            <>
              {/* Portal rings */}
              <motion.div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border-4 border-blue-400 opacity-80"
                animate={{ 
                  width: "150%",
                  height: "150%",
                  opacity: 0
                }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
              
              <motion.div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-blue-500 opacity-90"
                animate={{ 
                  width: "120%",
                  height: "120%",
                  opacity: 0
                }}
                transition={{ duration: 1.2, ease: "easeOut", delay: 0.1 }}
              />
              
              <motion.div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {/* Swirling particles */}
                {Array(12).fill(12).map((_, i) => (
                  <motion.div 
                    key={i}
                    className="absolute w-2 h-2 bg-blue-300 rounded-full"
                    style={{ 
                      left: "50%", 
                      top: "50%",
                      opacity: 0.7 
                    }}
                    animate={{ 
                      x: Math.cos(i * (Math.PI * 2 / 12)) * 60,
                      y: Math.sin(i * (Math.PI * 2 / 12)) * 60,
                      opacity: [0.7, 1, 0]
                    }}
                    transition={{ 
                      duration: 2, 
                      ease: "easeInOut",
                      times: [0, 0.5, 1], 
                      delay: i * 0.05,
                      repeat: 1
                    }}
                  />
                ))}
              </motion.div>
              
              {/* Cards appearing from portal */}
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                initial={{ scale: 0, opacity: 0, rotateZ: -20 }}
                animate={{ y: -70, scale: 1, opacity: 1, rotateZ: 0 }}
                transition={{ delay: 0.7, duration: 0.6, type: "spring" }}
              />
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                initial={{ scale: 0, opacity: 0, rotateZ: 10 }}
                animate={{ x: -60, y: -20, scale: 1, opacity: 1, rotateZ: -5 }}
                transition={{ delay: 0.9, duration: 0.6, type: "spring" }}
              />
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                initial={{ scale: 0, opacity: 0, rotateZ: 15 }}
                animate={{ x: 60, y: -20, scale: 1, opacity: 1, rotateZ: 5 }}
                transition={{ delay: 1.1, duration: 0.6, type: "spring" }}
              />
            </>
          )}
        </motion.div>
      )
    },
    {
      name: "Cascade Waterfall",
      animation: (
        <motion.div className="relative w-48 h-64">
          {/* Pack */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl shadow-xl overflow-hidden"
            animate={isOpening ? { y: -20, opacity: 0.8 } : { y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-xl">PACK</div>
            
            {/* Waterfall effect at bottom when opening */}
            {isOpening && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-b from-transparent to-blue-300 opacity-70"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.4 }}
              />
            )}
          </motion.div>
          
          {/* Cards cascading down */}
          {isOpening && (
            <>
              {/* Splashing water particles */}
              {Array(15).fill(15).map((_, i) => (
                <motion.div 
                  key={i}
                  className="absolute bottom-0 w-1.5 h-1.5 bg-blue-200 rounded-full"
                  initial={{ x: (Math.random() - 0.5) * 50, y: 100, opacity: 0.8 }}
                  animate={{ 
                    y: -50 - Math.random() * 100, 
                    opacity: 0 
                  }}
                  transition={{ 
                    duration: 0.8 + Math.random() * 0.5, 
                    delay: 0.3 + Math.random() * 0.3
                  }}
                />
              ))}
            
              {/* First card */}
              <motion.div 
                className="absolute w-20 h-32 bg-white rounded-lg shadow-lg left-1/2 -translate-x-1/2"
                initial={{ y: -30, opacity: 0, rotateX: -90 }}
                animate={{ y: -120, opacity: 1, rotateX: 0, x: 0 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300,
                  damping: 20,
                  delay: 0.6
                }}
              />
              
              {/* Second card */}
              <motion.div 
                className="absolute w-20 h-32 bg-white rounded-lg shadow-lg left-1/2 -translate-x-1/2"
                initial={{ y: -30, opacity: 0, rotateX: -90 }}
                animate={{ y: -70, opacity: 1, rotateX: 0, x: -60 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300,
                  damping: 20,
                  delay: 0.8
                }}
              />
              
              {/* Third card */}
              <motion.div 
                className="absolute w-20 h-32 bg-white rounded-lg shadow-lg left-1/2 -translate-x-1/2"
                initial={{ y: -30, opacity: 0, rotateX: -90 }}
                animate={{ y: -70, opacity: 1, rotateX: 0, x: 60 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300,
                  damping: 20,
                  delay: 1.0
                }}
              />
            </>
          )}
        </motion.div>
      )
    }
  ];

  return (
    <div className="flex flex-col items-center gap-8">
      <h2 className="text-2xl font-bold text-indigo-800">Pack Opening Animations</h2>
      
      <div className="flex flex-wrap justify-center gap-8">
        {packOpeningVariants.map((variant, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <h3 className="text-lg font-semibold mb-2">{variant.name}</h3>
            <div 
              className="border-2 border-gray-200 rounded-xl p-4 bg-white shadow-md hover:shadow-lg transition-all"
              onClick={() => {
                setOpeningVariant(idx);
                setIsOpening(true);
                setTimeout(() => setIsOpening(false), 2000);
              }}
            >
              {variant.animation}
            </div>
            <button 
              className="mt-2 px-4 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
              onClick={() => {
                setOpeningVariant(idx);
                setIsOpening(true);
                setTimeout(() => setIsOpening(false), 2000);
              }}
            >
              Play Animation
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Card Flip Demo Component
const CardFlipDemo = () => {
  const [activeVariant, setActiveVariant] = useState(0);
  
  const cardFlipVariants = [
    {
      name: "Basic Flip",
      component: () => {
        const [isFlipped, setIsFlipped] = useState(false);
        
        return (
          <motion.div
            className="w-24 h-36 rounded-lg shadow-lg cursor-pointer"
            onClick={() => setIsFlipped(!isFlipped)}
            animate={{ rotateY: isFlipped ? 180 : 0 }}
            transition={{ duration: 0.5 }}
            style={{ perspective: "1000px" }}
          >
            {/* Card Front */}
            <motion.div
              className="absolute w-full h-full bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center"
              style={{ 
                backfaceVisibility: 'hidden'
              }}
            >
              <div className="text-3xl text-red-600">♥</div>
            </motion.div>

            {/* Card Back */}
            <motion.div
              className="absolute w-full h-full bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg border-2 border-gray-200 flex items-center justify-center"
              style={{ 
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }}
            >
              <div className="text-white text-opacity-20 text-6xl font-bold">?</div>
            </motion.div>
          </motion.div>
        );
      }
    },
    {
      name: "3D Flip with Shadow",
      component: () => {
        const [isFlipped, setIsFlipped] = useState(false);
        
        return (
          <div className="relative" style={{ perspective: "1500px" }}>
            <motion.div
              className="w-24 h-36 rounded-lg cursor-pointer"
              onClick={() => setIsFlipped(!isFlipped)}
              animate={{ 
                rotateY: isFlipped ? 180 : 0,
                z: isFlipped ? 50 : 0
              }}
              transition={{ duration: 0.6, type: "spring", stiffness: 300, damping: 15 }}
            >
              {/* Shadow */}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-2 bg-black opacity-20 rounded-full blur-sm mx-2"
                animate={{ 
                  scaleX: isFlipped ? 0.7 : 1,
                  opacity: isFlipped ? 0.4 : 0.2
                }}
                transition={{ duration: 0.6 }}
              />
              
              {/* Card Front */}
              <motion.div
                className="absolute w-full h-full bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-lg"
                style={{ backfaceVisibility: 'hidden' }}
              >
                <div className="text-3xl text-red-600">A♥</div>
              </motion.div>

              {/* Card Back */}
              <motion.div
                className="absolute w-full h-full bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-lg"
                style={{ 
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)'
                }}
              >
                <div className="text-white text-opacity-20 text-6xl font-bold">?</div>
              </motion.div>
            </motion.div>
          </div>
        );
      }
    },
    {
      name: "Fold Flip",
      component: () => {
        const [isFlipped, setIsFlipped] = useState(false);
        
        return (
          <div className="relative" style={{ perspective: "1000px" }}>
            <motion.div
              className="w-24 h-36 cursor-pointer"
              onClick={() => setIsFlipped(!isFlipped)}
            >
              {/* Left half */}
              <motion.div
                className="absolute left-0 top-0 w-1/2 h-full bg-white rounded-l-lg border-2 border-r-0 border-gray-200 flex items-center justify-end overflow-hidden"
                animate={{ 
                  rotateY: isFlipped ? -90 : 0
                }}
                transition={{ duration: 0.4 }}
              >
                <div className="text-3xl text-red-600 mr-1">A</div>
              </motion.div>
              
              {/* Right half - front */}
              <motion.div
                className="absolute right-0 top-0 w-1/2 h-full bg-white rounded-r-lg border-2 border-l-0 border-gray-200 flex items-center justify-start overflow-hidden"
                animate={{ 
                  rotateY: isFlipped ? 90 : 0
                }}
                transition={{ duration: 0.4 }}
              >
                <div className="text-3xl text-red-600 ml-1">♥</div>
              </motion.div>
              
              {/* Left half - back */}
              <motion.div
                className="absolute left-0 top-0 w-1/2 h-full bg-indigo-700 rounded-l-lg border-2 border-r-0 border-gray-200 flex items-center justify-end overflow-hidden"
                initial={{ rotateY: 180 }}
                animate={{ 
                  rotateY: isFlipped ? 90 : 180
                }}
                transition={{ duration: 0.4 }}
              >
                <div className="text-white text-opacity-70 text-3xl mr-1">?</div>
              </motion.div>
              
              {/* Right half - back */}
              <motion.div
                className="absolute right-0 top-0 w-1/2 h-full bg-indigo-700 rounded-r-lg border-2 border-l-0 border-gray-200 flex items-center justify-start overflow-hidden"
                initial={{ rotateY: 180 }}
                animate={{ 
                  rotateY: isFlipped ? 270 : 180
                }}
                transition={{ duration: 0.4 }}
              >
                <div className="text-white text-opacity-70 text-3xl ml-1">?</div>
              </motion.div>
            </motion.div>
          </div>
        );
      }
    }
  ];

  const FlipVariant = cardFlipVariants[activeVariant].component;

  return (
    <div className="flex flex-col items-center gap-8">
      <h2 className="text-2xl font-bold text-indigo-800">Card Flip Animations</h2>
      
      <div className="flex justify-center gap-4 mb-4">
        {cardFlipVariants.map((variant, idx) => (
          <button
            key={idx}
            className={`px-3 py-1 rounded ${activeVariant === idx ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'}`}
            onClick={() => setActiveVariant(idx)}
          >
            {variant.name}
          </button>
        ))}
      </div>
      
      <div className="border-2 border-gray-200 rounded-xl p-8 bg-white shadow-md">
        <FlipVariant />
      </div>
      
      <div className="text-center text-gray-600">
        Click on the card to flip it!
      </div>
    </div>
  );
};

// Hover Effects Demo Component
const HoverEffectsDemo = () => {
  const hoverEffects = [
    {
      name: "Glow Effect",
      component: () => (
        <motion.div 
          className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md"
          whileHover={{ 
            scale: 1.05,
            boxShadow: "0 0 25px rgba(79, 70, 229, 0.6)"
          }}
        >
          <div className="text-3xl text-indigo-600">K♠</div>
        </motion.div>
      )
    },
    {
      name: "Float Up",
      component: () => (
        <motion.div 
          className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md"
          whileHover={{ 
            y: -15,
            transition: { type: "spring", stiffness: 300, damping: 10 }
          }}
        >
          <div className="text-3xl text-red-600">Q♥</div>
        </motion.div>
      )
    },
    {
      name: "Tilt Effect",
      component: () => (
        <motion.div 
          className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md"
          whileHover={{ 
            rotateX: 10,
            rotateY: 15,
            scale: 1.1,
            transition: { duration: 0.2 }
          }}
        >
          <div className="text-3xl text-indigo-600">J♣</div>
        </motion.div>
      )
    },
    {
      name: "Corner Peek",
      component: () => {
        const [isHovered, setIsHovered] = useState(false);
        
        return (
          <motion.div 
            className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md relative overflow-hidden"
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            whileHover={{ scale: 1.05 }}
          >
            <div className="text-3xl text-red-600">10♦</div>
            
            {/* Corner peek effect */}
            <motion.div
              className="absolute top-0 right-0 w-6 h-6 bg-white origin-bottom-left"
              animate={{ 
                rotate: isHovered ? 45 : 0
              }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            />
          </motion.div>
        );
      }
    },
    {
      name: "Pulse & Shimmer",
      component: () => {
        const [isHovered, setIsHovered] = useState(false);
        
        return (
          <motion.div 
            className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md relative overflow-hidden"
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            animate={{ 
              scale: isHovered ? [1, 1.02, 1, 1.02, 1] : 1
            }}
            transition={{ 
              repeat: isHovered ? Infinity : 0, 
              duration: 1.5
            }}
          >
            <div className="text-3xl text-green-600">A♣</div>
            
            {/* Shimmer effect */}
            {isHovered && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                initial={{ x: -100 }}
                animate={{ x: 200 }}
                transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
              />
            )}
          </motion.div>
        );
      }
    },
    {
      name: "Magnetic Pull",
      component: () => {
        const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
        const [isHovered, setIsHovered] = useState(false);
        
        const handleMouseMove = (e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setMousePosition({
            x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
            y: ((e.clientY - rect.top) / rect.height - 0.5) * 20
          });
        };
        
        return (
          <motion.div 
            className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md"
            onMouseMove={handleMouseMove}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            animate={{ 
              rotateY: isHovered ? mousePosition.x : 0,
              rotateX: isHovered ? -mousePosition.y : 0,
              scale: isHovered ? 1.1 : 1
            }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <div className="text-3xl text-indigo-600">7♠</div>
          </motion.div>
        );
      }
    },
    {
      name: "Rainbow Edge",
      component: () => {
        const [isHovered, setIsHovered] = useState(false);
        
        return (
          <div className="relative">
            {/* Rainbow border */}
            <motion.div 
              className="absolute -inset-0.5 rounded-lg"
              style={{
                background: "linear-gradient(45deg, #ff0000, #ff7300, #fffb00, #48ff00, #00ffd5, #002bff, #7a00ff, #ff00c8, #ff0000)",
                backgroundSize: "400% 400%",
                zIndex: -1
              }}
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: isHovered ? 1 : 0,
                backgroundPosition: isHovered ? ["0% 0%", "100% 100%"] : "0% 0%"
              }}
              transition={{ duration: 3, repeat: Infinity, repeatType: "mirror" }}
            />
            
            <motion.div 
              className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md z-10"
              onHoverStart={() => setIsHovered(true)}
              onHoverEnd={() => setIsHovered(false)}
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-3xl text-red-600">2♥</div>
            </motion.div>
          </div>
        );
      }
    },
    {
      name: "Card Shake",
      component: () => {
        const [isHovered, setIsHovered] = useState(false);
        
        return (
          <motion.div 
            className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md"
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            animate={isHovered ? {
              x: [0, -4, 4, -4, 4, 0],
              y: [0, 2, -2, 2, -2, 0],
              rotate: [0, -1, 2, -2, 1, 0]
            } : {}}
            transition={{
              duration: 0.5,
              repeat: isHovered ? Infinity : 0,
              repeatDelay: 2
            }}
          >
            <div className="text-3xl text-indigo-600">6♣</div>
          </motion.div>
        );
      }
    },
    {
      name: "Particle Trail",
      component: () => {
        const [isHovered, setIsHovered] = useState(false);
        const [particles, setParticles] = useState([]);
        
        const addParticle = () => {
          if (!isHovered) return;
          
          const newParticle = {
            id: Date.now(),
            x: (Math.random() - 0.5) * 30,
            y: (Math.random() - 0.5) * 40,
            scale: Math.random() * 0.5 + 0.5,
            color: `hsl(${Math.random() * 50 + 200}, 80%, 60%)`
          };
          
          setParticles(prev => [...prev, newParticle]);
          
          // Remove particle after animation
          setTimeout(() => {
            setParticles(prev => prev.filter(p => p.id !== newParticle.id));
          }, 1000);
        };
        
        useEffect(() => {
          let interval;
          if (isHovered) {
            interval = setInterval(addParticle, 150);
          }
          
          return () => {
            if (interval) clearInterval(interval);
          };
        }, [isHovered]);
        
        return (
          <motion.div
            className="relative"
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
          >
            {/* Particles */}
            {particles.map(particle => (
              <motion.div
                key={particle.id}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  left: "50%",
                  top: "50%",
                  backgroundColor: particle.color
                }}
                initial={{ 
                  opacity: 0.8,
                  x: 0,
                  y: 0,
                  scale: particle.scale
                }}
                animate={{ 
                  opacity: 0,
                  x: particle.x,
                  y: particle.y,
                  scale: 0
                }}
                transition={{ duration: 1 }}
              />
            ))}
            
            <motion.div 
              className="w-24 h-36 bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center shadow-md z-10"
              whileHover={{ scale: 1.05 }}
            >
              <div className="text-3xl text-red-600">9♦</div>
            </motion.div>
          </motion.div>
        );
      }
    },
    {
      name: "Evolving Card",
      component: () => {
        const [isHovered, setIsHovered] = useState(false);
        const [stage, setStage] = useState(0);
        
        useEffect(() => {
          let timeout;
          if (isHovered && stage < 3) {
            timeout = setTimeout(() => {
              setStage(prev => prev + 1);
            }, 700);
          } else if (!isHovered && stage > 0) {
            setStage(0);
          }
          
          return () => {
            if (timeout) clearTimeout(timeout);
          };
        }, [isHovered, stage]);
        
        const backgrounds = [
          "bg-white",
          "bg-gradient-to-br from-blue-50 to-blue-100",
          "bg-gradient-to-br from-blue-100 to-blue-200",
          "bg-gradient-to-br from-blue-300 to-indigo-400"
        ];
        
        const borders = [
          "border-gray-200",
          "border-blue-300",
          "border-blue-400",
          "border-indigo-500"
        ];
        
        const glows = [
          "",
          "shadow-md",
          "shadow-lg",
          "shadow-xl"
        ];
        
        const textClasses = [
          "text-indigo-600",
          "text-indigo-700",
          "text-indigo-800",
          "text-indigo-900 font-bold"
        ];
        
        return (
          <motion.div 
            className={`w-24 h-36 ${backgrounds[stage]} rounded-lg border-2 ${borders[stage]} flex items-center justify-center ${glows[stage]}`}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            whileHover={{ scale: 1.05 }}
            layout
          >
            <motion.div className={`text-3xl ${textClasses[stage]}`} layout>4♠</motion.div>
          </motion.div>
        );
      }
    }
  ];

  return (
    <div className="flex flex-col items-center gap-8">
      <h2 className="text-2xl font-bold text-indigo-800">Hover Effect Variations</h2>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
        {hoverEffects.map((effect, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <h3 className="text-lg font-semibold mb-2">{effect.name}</h3>
            <div className="p-6 border-2 border-gray-200 rounded-xl bg-white shadow-md hover:shadow-lg transition-all">
              {effect.component()}
            </div>
          </div>
        ))}
      </div>
      
      <div className="text-center text-gray-600 mt-4">
        Hover over each card to see the effect!
      </div>
    </div>
  );
};

// Directional Burst Demo Component
const DirectionalBurstDemo = () => {
  const [isOpening, setIsOpening] = useState(false);
  const [direction, setDirection] = useState('N');
  const [isFlipped, setIsFlipped] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 20
    });
  };
  
  const directions = {
    'N': { x1: -70, y1: -120, x2: 0, y2: -150, x3: 70, y3: -120 },
    'NE': { x1: 0, y1: -120, x2: 70, y2: -120, x3: 140, y3: -70 },
    'E': { x1: 70, y1: -70, x2: 140, y2: 0, x3: 70, y3: 70 },
    'SE': { x1: 70, y1: 70, x2: 140, y2: 70, x3: 70, y3: 140 },
    'S': { x1: -70, y1: 120, x2: 0, y2: 150, x3: 70, y3: 120 },
    'SW': { x1: -140, y1: 70, x2: -70, y2: 70, x3: -70, y3: 140 },
    'W': { x1: -140, y1: 0, x2: -70, y2: -70, x3: -70, y3: 70 },
    'NW': { x1: -140, y1: -70, x2: -70, y2: -120, x3: 0, y3: -120 }
  };
  
  const currentDir = directions[direction];
  
  return (
    <div className="flex flex-col items-center gap-8">
      <h2 className="text-2xl font-bold text-indigo-800">Directional Burst Showcase</h2>
      
      <div className="flex flex-wrap justify-center gap-2 mb-4">
        {Object.keys(directions).map(dir => (
          <button
            key={dir}
            className={`px-3 py-1 rounded ${direction === dir ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'}`}
            onClick={() => setDirection(dir)}
          >
            {dir}
          </button>
        ))}
      </div>
      
      <div className="border-2 border-gray-200 rounded-xl p-8 bg-white shadow-md flex flex-col items-center gap-6">
        <div className="relative w-64 h-64 flex items-center justify-center">
          {/* Pack */}
          <motion.div 
            className="relative w-32 h-44 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl shadow-xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setIsOpening(true);
              setTimeout(() => setIsOpening(false), 2000);
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center text-white font-bold">CLICK ME</div>
            
            {/* Directional indicator */}
            <div className="absolute top-2 left-2 px-2 py-1 bg-white bg-opacity-80 rounded-lg text-xs font-bold text-indigo-800">
              Direction: {direction}
            </div>
          </motion.div>
          
          {/* Burst Animation */}
          {isOpening && (
            <>
              {/* Particle effects */}
              {Array(20).fill(20).map((_, i) => {
                const angle = (Math.PI * 2) * (i / 20);
                // Adjust angle based on direction
                let adjustedAngle = angle;
                if (direction === 'N') adjustedAngle += Math.PI/2;
                if (direction === 'NE') adjustedAngle += Math.PI/4;
                if (direction === 'E') adjustedAngle += 0;
                if (direction === 'SE') adjustedAngle -= Math.PI/4;
                if (direction === 'S') adjustedAngle -= Math.PI/2;
                if (direction === 'SW') adjustedAngle -= 3*Math.PI/4;
                if (direction === 'W') adjustedAngle += Math.PI;
                if (direction === 'NW') adjustedAngle += 3*Math.PI/4;
                
                return (
                  <motion.div 
                    key={i}
                    className="absolute w-2 h-2 bg-yellow-300 rounded-full"
                    initial={{ x: 0, y: 0, opacity: 1 }}
                    animate={{ 
                      x: Math.cos(adjustedAngle) * 100,
                      y: Math.sin(adjustedAngle) * 100,
                      opacity: 0
                    }}
                    transition={{ duration: 0.8 + Math.random() * 0.4 }}
                  />
                );
              })}
            
              {/* Cards flying out in specified direction */}
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg cursor-pointer"
                initial={{ x: 0, y: 0, rotate: 0, scale: 0.5, opacity: 0 }}
                animate={{ x: currentDir.x1, y: currentDir.y1, rotate: -15, scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 20 }}
                onClick={() => setIsFlipped(!isFlipped)}
                onMouseMove={handleMouseMove}
                style={{ perspective: "1000px" }}
              >
                <motion.div
                  className="absolute w-full h-full backface-hidden"
                  animate={{ 
                    rotateY: isFlipped ? 180 : 0,
                    rotateX: mousePosition.y,
                    rotateZ: mousePosition.x/2,
                  }}
                  transition={{ duration: 0.6, type: "spring", stiffness: 300, damping: 15 }}
                >
                  {/* Front */}
                  <motion.div
                    className="absolute w-full h-full bg-white rounded-lg border-2 border-gray-200 flex items-center justify-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <div className="text-3xl text-red-600">A♥</div>
                  </motion.div>
                  
                  {/* Back */}
                  <motion.div
                    className="absolute w-full h-full bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg border-2 border-gray-200 flex items-center justify-center"
                    style={{ 
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)'
                    }}
                  >
                    <div className="text-white text-opacity-20 text-4xl font-bold">?</div>
                  </motion.div>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                initial={{ x: 0, y: 0, rotate: 0, scale: 0.5, opacity: 0 }}
                animate={{ x: currentDir.x2, y: currentDir.y2, rotate: 0, scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 300, damping: 20 }}
              />
              
              <motion.div 
                className="absolute w-20 h-32 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg"
                initial={{ x: 0, y: 0, rotate: 0, scale: 0.5, opacity: 0 }}
                animate={{ x: currentDir.x3, y: currentDir.y3, rotate: 15, scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 20 }}
              />
            </>
          )}
        </div>
        
        <button 
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          onClick={() => {
            setIsOpening(true);
            setTimeout(() => setIsOpening(false), 2000);
          }}
        >
          Trigger Animation
        </button>
        
        <div className="text-gray-600 text-center max-w-md">
          <p>Select a compass direction to control where cards burst. The first card uses magnetic pull hover effect and smooth 3D flip when clicked.</p>
        </div>
      </div>
    </div>
  );
};

export default CardAnimations;
